/**
 * @module @repo/global-login-sdk/markdown
 * Winziger, sicherer Markdown-Renderer für die Rechtsseiten — Tailwind-only und
 * ohne zusätzliche Dependency (eine Markdown-Bibliothek plus Sanitizer wäre eine
 * neue Abhängigkeit und bräuchte einen ADR).
 *
 * SICHERHEIT: Es gibt **kein** `dangerouslySetInnerHTML`. Alles wird als React-
 * Elemente aufgebaut, Textinhalte gehen als Kinder durch React und werden dabei
 * escaped. Enthält ein Baustein rohes HTML (`<script>…`), erscheint es als
 * sichtbarer Text und NIE als DOM-Element — DB-Inhalt kann also keinen Code
 * ausführen (dasselbe Prinzip wie „kein eval" in der Regel-Engine).
 * Link-Ziele laufen zusätzlich gegen eine Schema-Whitelist, damit ein
 * `javascript:`-Ziel nicht als `href` landet.
 *
 * Unterstützter Umfang — genau die Konstrukte, die der Baustein-Katalog nutzt:
 * ATX-Überschriften (`##`…), `**fett**`, `*kursiv*`, `` `code` ``, Links,
 * flache Listen (`-`/`*`/`+` und `1.`), Absätze und der harte Zeilenumbruch
 * (zwei Leerzeichen am Zeilenende).
 *
 * BEWUSST NICHT unterstützt: `_kursiv_`/`__fett__`. Die Rechtstexte enthalten
 * `snake_case`-Namen (`__cf_bm`) und die Fehlanzeige `[Angabe fehlt: kontakt_email]`
 * — Unterstrich-Auszeichnung würde beides zerreißen.
 */
import { type ReactNode } from 'react';
import { cn } from './cn';

/** Ein erkanntes Markdown-Konstrukt auf Blockebene. */
type MarkdownBlock =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; lines: readonly string[] }
  | { kind: 'list'; ordered: boolean; items: readonly string[] };

/** ATX-Überschrift: ein bis sechs `#`, dann ein Leerzeichen. */
const HEADING_PATTERN = /^(#{1,6})\s+(.*)$/;
/** Aufzählungszeichen einer unsortierten Liste. */
const BULLET_PATTERN = /^[-*+]\s+(.*)$/;
/** Aufzählungszeichen einer sortierten Liste. */
const ORDERED_PATTERN = /^\d+[.)]\s+(.*)$/;
/** Markdown-Zeilenumbruch: zwei oder mehr Leerzeichen am Zeilenende. */
const HARD_BREAK_PATTERN = /[ \t]{2,}$/;

/**
 * Inline-Konstrukte in Prioritätsreihenfolge: Link, `**fett**`, `` `code` ``,
 * `*kursiv*`. `**` steht vor `*`, damit Fettdruck nicht als zwei leere
 * Kursiv-Spannen zerfällt.
 */
const INLINE_PATTERN = /\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*([^*\n]+)\*\*|`([^`\n]+)`|\*([^*\n]+)\*/g;

/**
 * Whitelist erlaubter Link-Ziele. Nur `http(s)`, `mailto:`, `tel:`, absolute
 * App-Pfade und Anker. Alles andere — insbesondere `javascript:` und `data:` —
 * wird verworfen und nur der Linktext ausgegeben.
 */
const SAFE_URL_PATTERN = /^(?:https?:\/\/|mailto:|tel:|\/|#)/i;

/** Zerlegt den Quelltext in Blöcke (Überschrift, Absatz, Liste). */
function parseBlocks(source: string): MarkdownBlock[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] ?? '';
    if (line.trim() === '') {
      index += 1;
      continue;
    }

    const heading = HEADING_PATTERN.exec(line);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1]?.length ?? 1, text: heading[2] ?? '' });
      index += 1;
      continue;
    }

    const ordered = ORDERED_PATTERN.test(line);
    const itemPattern = ordered ? ORDERED_PATTERN : BULLET_PATTERN;
    if (ordered || BULLET_PATTERN.test(line)) {
      const items: string[] = [];
      while (index < lines.length) {
        const item = itemPattern.exec(lines[index] ?? '');
        if (!item) break;
        items.push(item[1] ?? '');
        index += 1;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index] ?? '';
      if (
        current.trim() === '' ||
        HEADING_PATTERN.test(current) ||
        BULLET_PATTERN.test(current) ||
        ORDERED_PATTERN.test(current)
      ) {
        break;
      }
      paragraph.push(current);
      index += 1;
    }
    blocks.push({ kind: 'paragraph', lines: paragraph });
  }

  return blocks;
}

/**
 * Baut die Inline-Auszeichnungen einer Textspanne als React-Elemente. Der Text
 * selbst geht immer als Kind durch React (also escaped) — hier entsteht niemals
 * ein HTML-String.
 *
 * @param text Die Textspanne (eine Zeile bzw. ein Listeneintrag).
 * @param keyPrefix Stabiles Präfix für die React-Keys der Fragmente.
 * @returns Die Kinder-Knoten der Spanne.
 */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let counter = 0;

  for (const match of text.matchAll(INLINE_PATTERN)) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    cursor = match.index + match[0].length;
    const key = `${keyPrefix}-i${counter}`;
    counter += 1;

    const [, linkText, linkHref, bold, code, italic] = match;
    if (linkText !== undefined && linkHref !== undefined) {
      // Unsicheres Schema → nur der Linktext, kein `href`. Der Inhalt bleibt
      // lesbar, das Ziel wird verworfen (statt es klickbar zu machen).
      if (!SAFE_URL_PATTERN.test(linkHref)) {
        nodes.push(<span key={key}>{renderInline(linkText, key)}</span>);
        continue;
      }
      nodes.push(
        <a
          key={key}
          href={linkHref}
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:no-underline focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:outline-none"
        >
          {renderInline(linkText, key)}
        </a>,
      );
      continue;
    }
    if (bold !== undefined) {
      nodes.push(
        <strong key={key} className="font-semibold">
          {renderInline(bold, key)}
        </strong>,
      );
      continue;
    }
    if (code !== undefined) {
      nodes.push(
        <code key={key} className="rounded bg-foreground/5 px-1 py-0.5 font-mono text-[0.9em]">
          {code}
        </code>,
      );
      continue;
    }
    if (italic !== undefined) {
      nodes.push(<em key={key}>{renderInline(italic, key)}</em>);
    }
  }

  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

/** Absatz-Kinder inkl. harter Zeilenumbrüche (`␣␣` am Zeilenende → `<br />`). */
function renderParagraph(lines: readonly string[], keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  lines.forEach((line, position) => {
    const hardBreak = HARD_BREAK_PATTERN.test(line);
    nodes.push(...renderInline(line.trimEnd(), `${keyPrefix}-l${position}`));
    if (position === lines.length - 1) return;
    // Harter Umbruch = eigene Zeile; sonst weicher Umbruch = Leerzeichen.
    nodes.push(hardBreak ? <br key={`${keyPrefix}-br${position}`} /> : ' ');
  });
  return nodes;
}

/** Tailwind-Klassen je Überschriftenebene (`text-*`-Stufen der Host-App). */
const HEADING_CLASS: Readonly<Record<number, string>> = {
  1: 'text-2xl font-semibold tracking-tight',
  2: 'text-xl font-semibold tracking-tight',
  3: 'text-base font-semibold',
  4: 'text-sm font-semibold',
  5: 'text-sm font-semibold',
  6: 'text-sm font-semibold',
};

/** Rendert eine Überschrift auf der passenden Ebene (`h1`…`h6`). */
function Heading(props: { level: number; children: ReactNode }): ReactNode {
  const className = cn('mt-2 first:mt-0 text-foreground', HEADING_CLASS[props.level]);
  switch (props.level) {
    case 1:
      return <h1 className={className}>{props.children}</h1>;
    case 2:
      return <h2 className={className}>{props.children}</h2>;
    case 3:
      return <h3 className={className}>{props.children}</h3>;
    case 4:
      return <h4 className={className}>{props.children}</h4>;
    case 5:
      return <h5 className={className}>{props.children}</h5>;
    default:
      return <h6 className={className}>{props.children}</h6>;
  }
}

/** Props von {@link Markdown}. */
export interface MarkdownProps {
  /** Markdown-Quelltext (bereits gerendert, also ohne `{{ platzhalter }}`). */
  source: string;
  /** Zusätzliche Utility-Klassen für den Container. */
  className?: string;
}

/**
 * Rendert Markdown als React-Elemente — ohne `dangerouslySetInnerHTML` und ohne
 * zusätzliche Dependency. Rohes HTML im Quelltext wird als Text ausgegeben, nie
 * interpretiert; Link-Ziele müssen die Schema-Whitelist passieren.
 *
 * Bewusst schlank gehalten: unterstützt genau die Konstrukte des
 * Rechtstext-Katalogs (siehe Modul-Doku) und ist kein allgemeiner
 * CommonMark-Renderer.
 *
 * @param props Siehe {@link MarkdownProps}.
 * @returns Der gerenderte Textblock (leerer Quelltext → `null`).
 */
export function Markdown(props: MarkdownProps): ReactNode {
  const blocks = parseBlocks(props.source);
  if (blocks.length === 0) return null;

  return (
    <div className={cn('flex flex-col gap-4 text-sm leading-relaxed', props.className)}>
      {blocks.map((block, index) => {
        const key = `b${index}`;
        if (block.kind === 'heading') {
          return (
            <Heading key={key} level={block.level}>
              {renderInline(block.text, key)}
            </Heading>
          );
        }
        if (block.kind === 'list') {
          const items = block.items.map((item, position) => (
            <li key={`${key}-li${position}`}>{renderInline(item, `${key}-li${position}`)}</li>
          ));
          return block.ordered ? (
            <ol key={key} className="flex list-decimal flex-col gap-1.5 pl-5">
              {items}
            </ol>
          ) : (
            <ul key={key} className="flex list-disc flex-col gap-1.5 pl-5">
              {items}
            </ul>
          );
        }
        return <p key={key}>{renderParagraph(block.lines, key)}</p>;
      })}
    </div>
  );
}
